import {
  Flex,
  Form,
  NumericFormatField,
  SubmitButton,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { useUpdateRemark } from '@/features/project/_hooks/project';
import { useForm, useId } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  AlurDanaModel,
  type IndonesiaData,
} from '../../../_models/indonesia-data';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type FormValues = z.infer<typeof AlurDanaModel>;

type Props = {
  initialValues: FormValues | undefined;
  initialIndonesiaData: IndonesiaData | undefined;
};

export const AlurDanaForm: FC<Props> = ({
  initialIndonesiaData,
  initialValues,
}) => {
  const { t } = useTranslation();
  const { mutateAsync: updateProject } = useUpdateRemark();
  const { id } = useId();
  const { openMessage } = useMessage();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues
      ? initialValues
      : {
          id: crypto.randomUUID(),
          number: 0,
          penerimaDana: null,
          ringkasanKegiatan: null,
          jumlahDana: null,
          metodeKontrakDll: null,
          jumlahPesertaTender: null,
          tingkatKemenanganTender: null,
        },
    resolver: zodResolver(AlurDanaModel),
  });
  const { setDrawerState } = useProjectDetailDrawerState();

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(async (values: FormValues) => {
        try {
          await updateProject({
            id,
            indonesiaData: {
              ...initialIndonesiaData,
              alurDana: initialValues
                ? initialIndonesiaData?.alurDana?.map((alurDana) =>
                    alurDana.id === initialValues.id ? values : alurDana,
                  )
                : [...(initialIndonesiaData?.alurDana ?? []), values],
            },
          });
          openMessage('success', 'success');
          setDrawerState({ open: false });
        } catch {
          openMessage('error', 'failed');
        }
      })}
    >
      <Flex vertical gap={16}>
        <NumericFormatField
          required
          name="number"
          label={t('number')}
          control={control}
          errors={errors}
        />
        <TextInputField
          name="penerimaDana"
          label={t('penerima-dana')}
          control={control}
          errors={errors}
        />
        <TextAreaInputField
          name="ringkasanKegiatan"
          label={t('ringkasan-kegiatan')}
          control={control}
          errors={errors}
          rows={3}
        />
        <NumericFormatField
          name="jumlahDana"
          label={t('jumlah-dana')}
          control={control}
          errors={errors}
        />
        <TextAreaInputField
          name="metodeKontrakDll"
          label={t('metode-kontrak-dll')}
          control={control}
          errors={errors}
        />
        <NumericFormatField
          name="jumlahPesertaTender"
          label={t('jumlah-peserta-tender')}
          control={control}
          errors={errors}
        />
        <NumericFormatField
          name="tingkatKemenanganTender"
          label={t('tingkat-kemenangan-tender')}
          control={control}
          errors={errors}
        />
        <div>
          <SubmitButton
            text={t('save')}
            loading={isSubmitting}
            disabled={!isDirty}
          />
        </div>
      </Flex>
    </Form>
  );
};
